// Units for numeric lab results (docs/plans/phase1-lab.md §5.1).
//
// A unit is stored twice: its UCUM code (what travels to FHIR and the Shared Health Record)
// and the display text printed on the report. The code comes from the person setting up
// the analyte; this module checks its syntax and suggests a display. It is not a full UCUM
// parser and it never converts between units: conversions depend on the substance, and
// Wodi does not invent factors.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export type UnitErrorCode =
  'unit.empty' | 'unit.too_long' | 'unit.invalid_character' | 'unit.unbalanced';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const UNIT_ERRORS: Record<UnitErrorCode, string> = {
  'unit.empty': 'Enter the unit as a UCUM code, for example mmol/L',
  'unit.too_long': 'A unit code is at most 40 characters',
  'unit.invalid_character': 'A UCUM code has no spaces and uses plain keyboard characters only',
  'unit.unbalanced': 'A bracket or brace in the unit is not closed',
};

const MAX_UNIT_LENGTH = 40;

const CLOSING: Record<string, string> = { '(': ')', '[': ']', '{': '}' };

/**
 * Checks what UCUM's grammar requires of every code: printable ASCII without spaces, and
 * brackets and braces that open and close in order. Annotations in braces may hold any
 * printable character except braces. Anything subtler is left to the terminology service.
 */
export function checkUcumCode(code: string): Result<string, UnitErrorCode> {
  if (code === '') return err('unit.empty');
  if (code.length > MAX_UNIT_LENGTH) return err('unit.too_long');
  const open: string[] = [];
  for (const char of code) {
    const point = char.codePointAt(0) ?? 0;
    if (point < 0x21 || point > 0x7e) return err('unit.invalid_character');
    const inAnnotation = open.at(-1) === '{';
    if (inAnnotation) {
      if (char === '{') return err('unit.unbalanced');
      if (char === '}') open.pop();
      continue;
    }
    if (char === '(' || char === '[' || char === '{') {
      open.push(char);
    } else if (char === ')' || char === ']' || char === '}') {
      const last = open.pop();
      if (last === undefined || CLOSING[last] !== char) return err('unit.unbalanced');
    }
  }
  return open.length === 0 ? ok(code) : err('unit.unbalanced');
}

const SUPERSCRIPT: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '-': '⁻',
};

function superscript(digits: string): string {
  return digits.replace(/[0-9-]/g, (d) => SUPERSCRIPT[d] ?? d);
}

/** Whole atoms whose usual printed form differs from the code. */
const ATOMS: Record<string, string> = {
  Cel: '°C',
  '[IU]': 'IU',
  '[iU]': 'IU',
  'u[IU]': 'µIU',
  'm[IU]': 'mIU',
  ug: 'µg',
  umol: 'µmol',
  uL: 'µL',
  um: 'µm',
  uU: 'µU',
  ueq: 'µeq',
};

function displayAtom(atom: string): string {
  const mapped = ATOMS[atom];
  if (mapped !== undefined) return mapped;
  // Annotations such as {cells} print as their words.
  const annotation = /^\{([^{}]*)\}$/.exec(atom);
  if (annotation) return annotation[1] ?? '';
  // Powers of ten: 10*9 or 10^9.
  const power = /^10[*^](-?\d+)$/.exec(atom);
  if (power) return `10${superscript(power[1] ?? '')}`;
  return atom;
}

/**
 * Splits a code at its division and multiplication operators, keeping them, but never
 * inside brackets or braces: `mL/min/{1.73_m2}` has an annotation with a dot in it.
 */
function splitUnitTerms(code: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  for (const char of code) {
    if (char === '[' || char === '{' || char === '(') depth += 1;
    if (char === ']' || char === '}' || char === ')') depth = Math.max(0, depth - 1);
    if (depth === 0 && (char === '/' || char === '.')) {
      parts.push(current, char);
      current = '';
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts;
}

/**
 * A suggested display for a unit code, e.g. `10*9/L` → "×10⁹/L", `umol/L` → "µmol/L",
 * `{cells}/uL` → "cells/µL". The person setting up the analyte can change it; a code this
 * doesn't recognise is shown as typed.
 */
export function suggestUnitDisplay(code: string): string {
  const shown = splitUnitTerms(code)
    .map((part) => {
      if (part === '/') return '/';
      if (part === '.') return '·';
      return displayAtom(part);
    })
    .join('');
  // A count per volume is written as a multiplier: ×10⁹/L.
  return /^10[*^]-?\d+[/.]/.test(code) ? `×${shown}` : shown;
}
